import type { MapareRow } from '@lyra/shared';
import { rowHasFinalClassification, isSupersededByExpansion } from './rows';

export interface RunProgress {
  classified: number;
  validated: number;
  total: number;
}

/**
 * The two honestly-named states a row can be in, per the 2026-08-19
 * remediation spec. `classified` = the pipeline (or a correction) produced a
 * final MEDOC code. `validated` = a human signed off.
 *
 * `validated` is deliberately STRICTER than `wizardBucket`'s `handled`, which
 * also accepts `reviewResolution != null` — that field is set by the pipeline
 * as well as by an operator, so it is not evidence anyone looked. Do not
 * "fix" the two to agree; they answer different questions.
 */
export function rowProgress(row: MapareRow): { classified: boolean; validated: boolean } {
  return {
    classified: rowHasFinalClassification(row),
    validated: row.validatedAt != null || row.status === 'edited',
  };
}

/**
 * Totals over the SAME population the table's sections and the wizard's steps
 * partition: pending rows (no result yet) and expansion-superseded parents
 * (their children carry the real classification) are excluded from all three
 * figures, exactly as `groupResults` excludes them.
 */
export function runProgress(rows: MapareRow[]): RunProgress {
  let classified = 0;
  let validated = 0;
  let total = 0;
  for (const row of rows) {
    if (row.status === 'pending') continue;
    if (isSupersededByExpansion(row)) continue;
    total += 1;
    const p = rowProgress(row);
    if (p.classified) classified += 1;
    if (p.validated) validated += 1;
  }
  return { classified, validated, total };
}
